import {SxProps, Theme} from '@mui/material';
import theme from '../../../theme';
import {pillShapePhoneSx} from 'src/components/common/pillFit.sx';

export const btnWrapperSx: SxProps<Theme> = {
    position: 'absolute',
    right: '16px',
    bottom: '10px',
    display: 'flex',
    gap: 0.5,
    alignItems: 'center',
    justifyContent: 'flex-end',
    // With the card grown to fit its message, there is no fixed corner to pin the actions to: they
    // sit under the text, where they are also out of the way of the tap that opens the notification.
    [theme.breakpoints.down('md')]: {
        position: 'static',
        justifyContent: 'flex-start',
        gap: 1,
    },
};
export const unreadSx: SxProps<Theme> = {
    p: 0.5,
    px: 1,
    minWidth: 'auto',
    fontSize: '11px',
    textTransform: 'none',
    fontWeight: 500,
};
export const selectBtnSx: SxProps<Theme> = {
    p: 0.5,
    px: 1,
    minWidth: 'auto',
    fontSize: '11px',
    textTransform: 'none',
    fontWeight: 500,
};
export const cardSx = (read: boolean, isSelected?: boolean): SxProps<Theme> => ({
    position: 'relative',
    backgroundColor: read ? '#F5F7FA' : 'rgba(0, 127, 220, 0.06)',
    borderRadius: '10px',
    py: 1.25,
    px: 2,
    height: '84px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    color: theme.palette.text.primary,
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    border: isSelected
        ? `1px solid ${theme.palette.primary.main}`
        : '1px solid transparent',
    '&:before': !read ? {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: '3px',
        height: '60%',
        borderRadius: '0 3px 3px 0',
        backgroundColor: theme.palette.primary.main,
    } : {},
    '&:hover': {
        backgroundColor: read ? '#EEF1F5' : 'rgba(0, 127, 220, 0.1)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },
    '& .markAsUnread-btn': {
        display: 'none',
    },
    '&:hover .markAsUnread-btn': {
        display: read ? 'inline-flex' : 'none',
    },
    '& .select-btn': {
        display: isSelected ? 'inline-flex' : 'none',
    },
    '&:hover .select-btn': {
        display: read ? 'none' : 'inline-flex',
    },
    [theme.breakpoints.down('md')]: {
        // The card as drawn: white, with its own edge and shadow, and as tall as what is in it. It
        // was a tinted 84px strip with `overflow: hidden`, which is why the message itself was one
        // clipped line - the thing the notification is actually about.
        alignItems: 'flex-start',
        gap: '10px',
        height: 'auto',
        p: '14px',
        borderRadius: '14px',
        backgroundColor: theme.palette.common.white,
        border: isSelected
            ? `1px solid ${theme.palette.primary.main}`
            : '1px solid rgba(0, 0, 0, 0.05)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
        // Unread is said by a strip down the whole left edge rather than a mark beside the avatar.
        '&:before': !read ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            transform: 'none',
            width: '3px',
            height: '100%',
            borderRadius: '3px 0 0 3px',
            backgroundColor: theme.palette.primary.main,
        } : {},
        // A finger cannot hover. Both actions are simply present, under the same conditions the
        // hover rules applied: "mark as unread" for a read one, "select" for any of them.
        '& .markAsUnread-btn': {display: read ? 'inline-flex' : 'none'},
        '&:hover .markAsUnread-btn': {display: read ? 'inline-flex' : 'none'},
        '& .select-btn': {display: 'inline-flex'},
        '&:hover .select-btn': {display: 'inline-flex'},
    },
});
export const dateBoxLgSx: SxProps<Theme> = {
    display: 'block',
    [theme.breakpoints.down('lg')]: {
        display: 'none',
    },
};
export const dateBoxSmSx: SxProps<Theme> = {
    display: 'none',
    [theme.breakpoints.down('lg')]: {
        display: 'block',
    },
};
export const detailsBoxSx: SxProps<Theme> = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0.5,
    color: theme.palette.neutral[400],
    overflow: 'hidden',
};
export const readBoxSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    overflow: 'hidden',
    minWidth: 0,
};
export const visibilitySx: SxProps<Theme> = {
    color: theme.palette.neutral[400],
    width: '13px',
    height: '13px',
    flexShrink: 0,
};
export const readByTextSx: SxProps<Theme> = {
    fontSize: '11px',
    fontWeight: 400,
    color: theme.palette.neutral[400],
    whiteSpace: 'nowrap',
};
export const dotSepSx: SxProps<Theme> = {
    fontSize: '11px',
    color: theme.palette.neutral[300],
    flexShrink: 0,
};
export const dateSx: SxProps<Theme> = {
    fontSize: '11px',
    fontWeight: 400,
    color: theme.palette.neutral[400],
    textAlign: 'right',
    lineHeight: 1.3,
    [theme.breakpoints.down('lg')]: {
        textAlign: 'left',
    },
};
export const cardTextSx: SxProps<Theme> = {
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.neutral[500],
    lineHeight: 1.35,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '520px',
};
export const titleTextSx: SxProps<Theme> = {
    color: theme.palette.neutral[400],
    fontSize: '10px',
    mb: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    display: 'none',
};
export const roleTextSx: SxProps<Theme> = {
    fontSize: '9px',
    fontWeight: 600,
    width: 'fit-content',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.primaryColor[100],
    px: 0.75,
    py: 0.15,
    ml: 1,
    borderRadius: '4px',
    display: 'inline-block',
    verticalAlign: 'middle',
    // The kind of notification, as the pill every other card wears.
    [theme.breakpoints.down('md')]: {
        ...pillShapePhoneSx,
        ml: 1,
    },
};
export const colSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    minWidth: 0,
    flex: 1,
};
export const textBoxSx: SxProps<Theme> = {
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
    gap: 2,
    minWidth: 0,
    overflow: 'hidden',
    // A column on a phone: two columns of text on a 390px screen leaves neither of them a width.
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        gap: 0.75,
        overflow: 'visible',
    },
};

export const leftColSx: SxProps<Theme> = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 0.25,
    minWidth: 0,
    overflow: 'hidden',
};

export const rightColSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
    flexShrink: 0,
    gap: 0.25,
    pb: '28px',
    // No longer a right-hand column: the date and who read it follow the message, left-aligned with
    // it, and the 28px that made room for the pinned buttons is not needed once they are in flow.
    [theme.breakpoints.down('md')]: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-start',
        columnGap: 1,
        pb: 0,
    },
};
export const newMarkSx: SxProps<Theme> = {
    display: 'none',
};
export const avatarBoxSx: SxProps<Theme> = {
    position: 'relative',
    flexShrink: 0,
    alignSelf: 'center',
};
export const avatarSx: SxProps<Theme> = {
    width: '36px',
    height: '36px',
    fontSize: '14px',
};
export const nameRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    minWidth: 0,
};
export const nameTextSx = (read: boolean): SxProps<Theme> => ({
    fontSize: '13px',
    fontWeight: read ? 500 : 700,
    color: read ? theme.palette.neutral[500] : theme.palette.text.primary,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    // The card's title, at the size every other card's title is.
    [theme.breakpoints.down('md')]: {
        fontSize: '15px',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
});

export const cardTextReadSx = (read: boolean): SxProps<Theme> => ({
    fontSize: '12px',
    fontWeight: 400,
    color: read ? theme.palette.neutral[400] : theme.palette.neutral[600],
    lineHeight: 1.35,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '520px',
    // Two lines rather than one clipped one. This is the message itself - the reason the card is on
    // the screen at all - and on a phone a single nowrap line showed perhaps four words of it.
    [theme.breakpoints.down('md')]: {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        whiteSpace: 'normal',
        maxWidth: 'none',
        lineHeight: 1.45,
        color: theme.palette.text.primary,
    },
});
